import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/hooks/use-toast';
import { getAccountId } from '@/lib/obfuscate';
import { secureSetItem } from '@/lib/storage';
import { RotateCcw, Loader2 } from 'lucide-react';

interface AccountIdModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AccountIdModal = ({ isOpen, onOpenChange }: AccountIdModalProps) => {
  const { accountId, reloadData, fetchAccountIdByDealer } = useAppData();
  const { colors } = useTheme();
  const { toast } = useToast();
  const [dealerIdInput, setDealerIdInput] = useState<string>('');
  const [originalAccountId, setOriginalAccountId] = useState<string | null>(null);
  const [isSubmittingDealer, setIsSubmittingDealer] = useState(false);

  // Get original account ID from environment
  useEffect(() => {
    const envAccountId = getAccountId();
    setOriginalAccountId(envAccountId);
  }, []);

  // Reset dealer input when modal opens
  useEffect(() => {
    if (isOpen) {
      setDealerIdInput('');
    }
  }, [isOpen]);

  const titleColor = colors?.title || '#000000';
  const buttonColor = colors?.button || '#000000';

  const handleApplyDealer = async () => {
    if (!dealerIdInput || dealerIdInput.trim() === '') {
      toast({
        title: "Alerta",
        description: "Informe o Dealer ID para buscar a conta.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmittingDealer(true);
      if (originalAccountId) {
        await secureSetItem('accountId', originalAccountId);
        localStorage.clear();
      }

      const result = await fetchAccountIdByDealer(dealerIdInput.trim());
      setIsSubmittingDealer(false);

      if (result.success && result.accountId) {
        onOpenChange(false);
        setTimeout(() => {
          window.location.reload();
        }, 200);
      } else {
        toast({
          title: "Dealer ID não encontrado!",
          description: result.message || "Verifique o Dealer ID informado.",
          variant: "destructive"
        });
      }
    } catch (error) {
      setIsSubmittingDealer(false);
      toast({
        title: "Erro",
        description: "Não foi possível buscar o Dealer ID. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  const handleReset = async () => {
    if (!originalAccountId) {
      toast({
        title: "Erro",
        description: "ID da Conta original não encontrado.",
        variant: "destructive"
      });
      return;
    }

    try {
      await secureSetItem('accountId', originalAccountId);

      onOpenChange(false);

      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível resetar o ID da Conta.",
        variant: "destructive"
      });
    }
  };

  const isResetAvailable = originalAccountId && accountId !== originalAccountId;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-full">
        <DialogHeader>
          <DialogTitle style={{ color: titleColor }}>Recarregar Website</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dealer-id">Dealer ID</Label>
            <Input
              id="dealer-id"
              type="text"
              value={dealerIdInput}
              onChange={(e) => setDealerIdInput(e.target.value)}
              placeholder="Digite seu Dealer ID aqui"
              className="font-mono"
            />
            <p className="text-sm text-muted-foreground">
              Informe seu Dealer ID para que o website seja recarregado.
            </p>
          </div>

          {/* <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Account ID atual:</p>
            <p className="text-sm font-mono font-medium">{accountId || 'Não definido'}</p>
          </div> */}

          {/* {originalAccountId && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">ID Original:</p>
              <p className="text-sm font-mono font-medium">{originalAccountId}</p>
            </div>
          )} */}

          <div className="flex gap-2">
            {isResetAvailable && (
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Resetar
              </Button>
            )}
            <Button
              onClick={handleApplyDealer}
              disabled={isSubmittingDealer}
              className="flex-1 flex items-center justify-center gap-2"
              style={{
                backgroundColor: buttonColor,
                color: '#fff'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {isSubmittingDealer && <Loader2 className="h-4 w-4 animate-spin" />}
              Buscar Conta
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

